<script lang="ts">
	//Svelte
	import { beforeUpdate, onDestroy, onMount } from 'svelte';
	//Services
	import { format } from 'svelte-i18n';
	import { postReq } from '../../../services/http';
	//Stores
	import { devices } from 'src/stores/lists/devices';
	import { alarms } from 'src/stores/lists/scenes-derived';
	import { is_mobile, is_tablet } from 'src/stores/ui/viewport';
	import { edit } from 'src/stores/ui/edit_lists';
	//Helpers
	import { load_image_from } from '../../../helpers/interface_helpers';
	import {
		onStartDrag,
		onEndDrag,
		filterSensors,
		requestReorderDevice,
		getAllAlarmsGroupedDevices,
		checkIsAllAlarmsArmed,
		type AlarmDelayModalType,
		type AlarmDelayTimerType,
	} from '../list-helpers';
	import {
		alarm_object_prototype,
		ALARM_TIME_TRIGGER_DEFAULT,
		type IAlarmZone,
	} from '../../AlarmZones/AlarmWizard/alarm_object';
	import { capFirstChar, _deepClone } from '../../../helpers/utilities';
	//Design System Components
	import {
		CardGrid,
		Button,
		Toolbar,
		Breadcrumbs,
		IconButton,
		Input,
		toast,
	} from '@Shelly/shelly-design-system';
	//Components
	import AddEditButtons from '../../AddEditButtons.svelte';
	import EmptyList from '../EmptyList.svelte';
	import ListEmptyStateSurface from '../ListEmptyStateSurface.svelte';
	import EmptyDeviceTypeList from '../EmptyDeviceTypeList.svelte';
	import AlarmWizard from '../../AlarmZones/AlarmWizard/AlarmWizard.svelte';
	import PinUnlock from 'src/components/PinUnlock.svelte';
	import AlarmArmAllModal from './components/AlarmArmAllModal.svelte';
	import AlarmBreachedSensorModal from './components/AlarmBreachedSensorModal.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import { alarmMainFilter } from 'src/stores/ui/filters';
	import { createListHeaderHandle, type ListHeaderConfig } from 'src/stores/list_header';
	import AlarmListContent from './AlarmListContent.svelte';
	import ListSkeletonGrid from '../ListSkeletonGrid.svelte';
	import { forceListSkeleton } from 'src/stores/features/dev/force-list-skeleton';
	import AlarmDeleteModal from './AlarmDeleteModal.svelte';
	import { clearAlarmItemNewFlag } from 'src/helpers/add-entity-helpers';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import { homes } from 'src/stores/account/home';
	import SharedHomeOwnerManagedModal from 'src/components/SharedHome/SharedHomeOwnerManagedModal.svelte';
	import {
		getSharedHomeEmptyListCopy,
		type SharedHomeManagedContext,
	} from 'src/helpers/houses/shared_home_messaging_helpers';

	export let list;
	export let type = 'alarms';
	// export let id;
	const REQUEST_TYPE = 'scene';
	const FLIP_DURATION_MS = 0;

	const headerHandle = createListHeaderHandle();

	let alarm_id;
	let alarm_name = '';
	let turnType: boolean;

	let showAddEditAlarmModal = false;
	let showSharedManagedModal = false;
	let sharedManagedContext: SharedHomeManagedContext = 'add_alarm';
	let showDeleteAlarmModal = false;
	let dragDisabled = true;
	let currentRoute = '';
	let sensors = [];
	let alarm_object = _deepClone(alarm_object_prototype);
	let showPinLockModal = false;

	const handle_add_alarm = () => {
		if (isActiveHomeShared($homes.list)) {
			sharedManagedContext = 'add_alarm';
			showSharedManagedModal = true;
			return;
		}
		showAddEditAlarmModal = true;
		alarm_id = '';
		alarm_object = _deepClone(alarm_object_prototype);
	};
	let alarmsDelayModal: AlarmDelayModalType = {};
	let alarmsDelayTimer: AlarmDelayTimerType = {};
	let showArmingAllModal = false;
	let showBreachedModal = false;

	$: isEmptyDeviceList = Object.values($devices).length === 0;
	$: isEmptySensorsList = sensors.length === 0;
	$: items = $edit && currentRoute === type ? items : [...list];
	$: dndSettings = {
		items,
		flipDurationMs: FLIP_DURATION_MS,
		dragDisabled: dragDisabled,
	};

	function toggleAlarmsWithPin(items, enabled) {
		// this function check for situation when if we have for example two alarms. one alarm is with pin and it is enabled, the other is without pin and it is disabled.
		// if we want to enable all alarms in this case the pin modal should not appear, because the inactive(disabled) alarm doesn't have pin lock
		// but if we want to disable all alarms, the pin modal shoul appear, because the active(enabled) alarm has pin lock
		// enabled is 'true' if there is active alarm and should be inactivated, and is 'false' if there is inactive alarm and should be activated
		let filtratedItem = items.filter((item) => item.pin_lock && item._enabled === enabled);
		return filtratedItem.length > 0;
	}

	onMount(async () => {
		currentRoute = type;

		sensors = [...filterSensors(Object.values($devices))];
	});

	onDestroy(() => {
		edit.set(false);
		/*
		 * Intentionally clears `_meta.new` for every alarm in the scenes store (global), not
		 * room-scoped like `clearGroupsItemNewFlag` / `clearScenesItemNewFlag` / `clearThermostatsItemNewFlag`.
		 * Leaving the Alarms screen should not leave stale pulse state; alarms are not split by room here.
		 */
		clearAlarmItemNewFlag();
		headerHandle.clear();
	});

	beforeUpdate(() => {
		if (sensors.length === 0) {
			sensors = [...filterSensors(Object.values($devices))];
		}

		if (!$edit) handleEndDrag();

		if (currentRoute !== type) {
			currentRoute = type;
		}
	});

	const onCardClick = (item) => {
		if ($edit) return;

		setSidePanelState({ type: 'alarm', id: item._id, open: true });
	};

	const onCardEdit = async (event: MouseEvent | TouchEvent, item) => {
		event.stopPropagation();
		alarm_id = item._id;

		alarm_name = item?._meta?.name || 'unknown';
		alarm_object = _deepClone($alarms[alarm_id]);
		showAddEditAlarmModal = true;
	};

	const onCardDelete = (event: MouseEvent | TouchEvent, item) => {
		event.stopPropagation();
		alarm_id = item._id;

		alarm_name = item?._meta?.name || 'unknown';
		showDeleteAlarmModal = true;
	};

	// DND Functions
	const handleStartDrag = () => {
		dragDisabled = false;
	};

	const handleEndDrag = () => {
		dragDisabled = true;
	};

	const handleReorder = (e: CustomEvent) => {
		items = e.detail.items;
	};

	const handleReorderEnd = async (e: CustomEvent) => {
		let editedList = {};
		e.detail.items.map((v, i) => {
			delete v?.isDndShadowItem;
			v.id = String(v.id);
			v._id = String(v._id);
			v._name = String(v._id);

			editedList[v.id] = {
				...v,
				_meta: {
					...v._meta,
					position: i,
				},
			};
		});

		if (Object.keys(editedList).length === 0) return;

		items = [...e.detail.items];
		handleEndDrag();

		await requestReorderDevice(REQUEST_TYPE, editedList);
	};

	const handleLocked = () => {
		let alarmHasPin = toggleAlarmsWithPin(items, false);
		if (alarmHasPin) {
			showPinLockModal = true;
			turnType = true;
		}
		if (!alarmHasPin) {
			handleAllAlarms(true);
		}
	};

	const interceptSharedHomeAlarmControl = (): boolean => {
		if (!isActiveHomeShared($homes.list)) {
			return false;
		}

		sharedManagedContext = 'control_alarm';
		showSharedManagedModal = true;
		return true;
	};

	const turnOnAll = () => {
		if (interceptSharedHomeAlarmControl()) return;

		const alarmGroupedDevices = getAllAlarmsGroupedDevices(items);
		const isAllArmed = checkIsAllAlarmsArmed(items, false);
		if (
			(alarmGroupedDevices?.breached?.length || alarmGroupedDevices?.offline?.length) &&
			!isAllArmed
		) {
			showBreachedModal = true;
		} else {
			handleLocked();
		}
	};

	const turnOffAll = () => {
		if (interceptSharedHomeAlarmControl()) return;

		let alarmHasPin = toggleAlarmsWithPin(items, true);
		if (alarmHasPin) {
			showPinLockModal = true;
			turnType = false;
		}

		if (!alarmHasPin) {
			handleAllAlarms(false);
		}
	};

	const handleAllAlarms = async (isEnabled: boolean) => {
		if (interceptSharedHomeAlarmControl()) return;

		try {
			showArmingAllModal = isEnabled;
			const alarmIdsEditList = Object.values($alarms)
				.map((alarm: IAlarmZone) => {
					if (!alarm._enabled === isEnabled) {
						return alarm._id;
					}
				})
				.filter((alarmID) => alarmID);

			if (!alarmIdsEditList.length) return;

			const res = await postReq('scene/bulk_meta_update', {
				ids: JSON.stringify(alarmIdsEditList),
				enabled: isEnabled ? 1 : 0,
				meta: JSON.stringify({
					_time_trigger: isEnabled ? Date.now() : ALARM_TIME_TRIGGER_DEFAULT,
				}),
			});

			if (res.isok) {
				//TODO UPDATE STORE WITH BULK UPDATE. ASK TO UPDATE RES
				// scenes.bulkEditScenes(res.data.scene_script);
				if (!isEnabled) {
					toast.open({
						type: 'info',
						title: $format('info_arm_disarm_alarms', {
							values: {
								value: $format(`alarms_${isEnabled ? 'armed' : 'disarmed'}`),
							},
						}),
					});
				}

				turnType = undefined;
			} else {
				toast.open({
					type: 'danger',
					title: $format('error_global'),
				});
			}
		} catch (error) {
			toast.open({
				type: 'danger',
				title: $format('error_global'),
			});
			turnType = undefined;
		}
	};

	function handleClose() {
		showPinLockModal = false;
	}

	// New-layout kebab: Arm all / Disarm all / Reorder items. During edit mode,
	// a "Finish reordering" primary action replaces the kebab. Search lives in
	// the kebab on mobile/tablet (same as Rooms); desktop keeps the Toolbar input.
	$: reorderDisabled = items.length <= 1;
	$: hideInlineSearch = $is_mobile || $is_tablet;
	$: if ($showNewLayout) {
		const config: ListHeaderConfig = $edit
			? {
					primaryAction: {
						label: $format('label_finish_reordering', { default: 'Finish reordering' }),
						icon: 'fas fa-xmark',
						variant: 'secondary',
						onClick: () => edit.set(false),
					},
				}
			: {
					menuItems: [
						{
							items: [
								{
									title: $format('button_arm_all_alarms', {
										default: 'Arm all alarms',
									}),
									icon: 'fas fa-shield',
									iconStyle: 'color: var(--color-success);',
									onClick: () => turnOnAll(),
								},
								{
									title: $format('button_disarm_all_alarms', {
										default: 'Disarm all alarms',
									}),
									icon: 'fas fa-shield',
									iconStyle: 'color: var(--color-danger);',
									onClick: () => turnOffAll(),
								},
								{
									title: $format('label_reorder_items', {
										default: 'Reorder items',
									}),
									icon: 'fa-solid fa-arrows-up-down-left-right',
									disabled: reorderDisabled,
									onClick: () => edit.set(true),
								},
							],
						},
					],
					search: hideInlineSearch
						? {
								value: $alarmMainFilter,
								label: capFirstChar($format('search', { default: 'Search' })),
								id: 'alarms-kebab-search-input',
								onInput: (value) => alarmMainFilter.set(value),
							}
						: null,
				};
		headerHandle.set(config);
	}
</script>

{#if $showNewLayout}
	{#if !$edit && !hideInlineSearch}
		<Toolbar class="mt-m mb-m" isSticky useNewLayout noBreadcrumbs hideSlotRightSide>
			<svelte:fragment slot="leftSide">
				<Input
					class="text-capitalize-placeholder width-md-l"
					bind:value={$alarmMainFilter}
					type="text"
					label={capFirstChar($format('search', { default: 'Search' }))}
					icon="fas fa-search"
					id="alarms-toolbar-search-input"
					aria-labelledby="alarms-toolbar-search-input"
				/>
			</svelte:fragment>
		</Toolbar>
	{/if}
{:else}
	<Toolbar class="mb-m" isSticky hideSlotLeftSide>
		<svelte:fragment slot="breadcrumbs">
			<Breadcrumbs
				aria-label={$format('label_alarms', { default: 'Alarms' })}
				links={[{ label: capFirstChar($format('tab_alarms')) }]}
			/>
		</svelte:fragment>

		<svelte:fragment slot="rightSide">
			<IconButton
				aria-label={$format('button_arm_all_alarms', {
					default: 'Button arm all alarms',
				})}
				variant="secondary"
				icon="fas fa-shield text-success"
				on:click={turnOnAll}
			/>
			<IconButton
				aria-label={$format('button_disarm_all_alarms', {
					default: 'Button disarm all alarms',
				})}
				variant="secondary"
				icon="fas fa-shield text-danger"
				on:click={turnOffAll}
			/>

			<AddEditButtons
				disableEditButton={items.length <= 0}
				disableAddButton={isEmptyDeviceList || isEmptySensorsList}
				interceptEditClick={isActiveHomeShared($homes.list)}
				on:add_click={() => handle_add_alarm()}
				on:edit_click={() => {
					sharedManagedContext = 'edit_entity';
					showSharedManagedModal = true;
				}}
			/>
		</svelte:fragment>
	</Toolbar>
{/if}

<!-- {#if isEmptyDeviceList && Object.values($alarms).length <= 0}
	<EmptyDeviceList />
	{/if} -->
{#if items.length <= 0 && !!$alarmMainFilter}
	<ListEmptyStateSurface class="mt-md-l mt-sm-s">
		<EmptyList
			icon="fal fa-magnifying-glass"
			title={$format('no_such_alarm_found', { default: 'No such alarms found.' })}
			description={$format('check_the_spelling_or_try_new_search', {
				default: 'Check the spelling or try a new search.',
			})}
		/>
	</ListEmptyStateSurface>
{:else if items.length <= 0 && isActiveHomeShared($homes.list)}
	{@const sharedEmptyCopy = getSharedHomeEmptyListCopy('alarms')}
	<ListEmptyStateSurface class="mt-md-l mt-sm-s">
		<EmptyList
			icon="fal fa-bell"
			title={$format(sharedEmptyCopy.titleKey, { default: sharedEmptyCopy.titleDefault })}
			description={$format(sharedEmptyCopy.descrKey, {
				default: sharedEmptyCopy.descrDefault,
			})}
		/>
	</ListEmptyStateSurface>
{:else if isEmptySensorsList && Object.values($alarms).length <= 0}
	<ListEmptyStateSurface class="mt-md-l mt-sm-s">
		<EmptyDeviceTypeList icon="fal fa-bell" />
	</ListEmptyStateSurface>
{:else if items.length <= 0}
	<ListEmptyStateSurface class="mt-md-l mt-sm-s">
		<EmptyList
			icon="fal fa-bell"
			title={$format('no_alarms_title', {
				default: 'There are no alarms yet',
			})}
			description={$format('no_alarms_descr', {
				default: 'Try creating an Alarm for better safety and alertness.',
			})}
			btnText={$format('button_add_alarm', {
				default: 'Add alarm',
			})}
			onClick={() => handle_add_alarm()}
		/>
	</ListEmptyStateSurface>
{:else if $forceListSkeleton}
	<ListSkeletonGrid
		class={$edit && $showNewLayout ? 'mt-s' : ''}
		variant="list-menu"
		listMenuType="compact"
	/>
{:else}
	<CardGrid
		class={$edit && $showNewLayout ? 'mt-s' : ''}
		editMode={$edit}
		xl={350}
		lg={290}
		sm={270}
		{dndSettings}
		on:consider={handleReorder}
		on:finalize={handleReorderEnd}
	>
		{#each items as item, _ (item)}
			<div
				class:dnd-placeholder={item.isDndShadowItem}
				tabindex="0"
				role="button"
				aria-label={$format('button_drag_alarm_to_reorder', {
					default: 'Drag alarm to reorder',
				})}
			>
				<AlarmListContent
					{item}
					{load_image_from}
					{onCardClick}
					{onCardDelete}
					{onCardEdit}
					{alarmsDelayModal}
					{alarmsDelayTimer}
					on:handle-drag-start={(e) => onStartDrag(e, $edit, handleStartDrag, true)}
					on:handle-drag-end={(e) => onEndDrag(e, $edit, handleEndDrag, true)}
					on:mousedown={(e) => onStartDrag(e, $edit, handleStartDrag)}
					on:touchstart={(e) => onStartDrag(e, $edit, handleStartDrag)}
					on:mouseup={(e) => onEndDrag(e, $edit, handleEndDrag)}
					on:touchend={(e) => onEndDrag(e, $edit, handleEndDrag)}
				/>
			</div>
		{/each}
	</CardGrid>
{/if}

{#if showAddEditAlarmModal}
	<AlarmWizard bind:id={alarm_id} bind:showAddEditAlarmModal {alarm_object} />
{/if}

<AlarmDeleteModal bind:showDeleteAlarmModal bind:items {alarm_id} {alarm_name} />

<SharedHomeOwnerManagedModal bind:showModal={showSharedManagedModal} context={sharedManagedContext} />

{#if showPinLockModal}
	<PinUnlock
		bind:showModal={showPinLockModal}
		on:close={handleClose}
		on:unlock={() => handleAllAlarms(turnType)}
		on:cancel={() => (showPinLockModal = false)}
	/>
{/if}

{#if !showPinLockModal}
	<AlarmArmAllModal bind:showModal={showArmingAllModal} {alarmsDelayTimer} />
{/if}

{#if showBreachedModal}
	<AlarmBreachedSensorModal
		alarm={undefined}
		alarmsList={items}
		showItemsSubtitle={true}
		bind:showModal={showBreachedModal}
		on:armAlarm={handleLocked}
	/>
{/if}
